
import MissionTabs from '@/components/Mission/MissionTabs.vue';
import Component from 'vue-class-component';
import MissionTabNames from '@/flightpath-components/services/missionTabNames';
import SingleMachineMap from '@/components/Map/SingleMachineMap.vue';
import { DeployedSinglePathMission } from '@/flightpath-components/models/DeployedSinglePathMission';
import { getModule } from 'vuex-module-decorators';
import MissionStore from '@/store/modules/missions';
import { FlightPathServiceInterface } from '@/flightpath-components/services/FlightPathServiceInterface';
import { Container } from 'inversify';
import SERVICE_IDENTIFIER from '@/flightpath-components/services/identifiers';
import MachineStore from '@/store/modules/machine';
import { Mixins, Watch } from 'vue-property-decorator';
import { ImplementOperationsModel } from '@/flightpath-components/models/implement/ImplementOperationsModel';
import { ImplementKeys, Parameters } from '@/services/Parameters';
import { SortHelper } from '@/services/SortHelper';
import LastModifiedMixIn from '@/mixins/lastModified';
import { NotificationMessageType } from '@/flightpath-components/models/NotificationMessageType';
import MqttMixIn from '@/mixins/mqttManagement';
import { PubSub } from '@/PubSubClass';
import { ConvertMissionStatus } from '@/flightpath-components/models/mqttPayload/MissionStatusAutoTransitionMessage';
import {} from '@/flightpath-components/models/DeployedSinglePathMission';
import MissionToolTypes from '@/services/MissionToolTypes';
import GlobalStore from '@/store/modules/global';
import { Guid } from 'guid-typescript';
import { toNumber } from 'lodash';

// Define the component in class-style
/* eslint-disable @typescript-eslint/no-explicit-any */
@Component({ name: 'InProgressMissions', inject: ['container'], components: { MissionTabs, SingleMachineMap } })
export default class InProgressMissions extends Mixins(LastModifiedMixIn, MqttMixIn) {
  globalStore = getModule(GlobalStore, this.$store);
  tabName = MissionTabNames.InProgressMissions;
  type = [NotificationMessageType.MissionStart];
  loading = true;
  panel = [0];
  missions: DeployedSinglePathMission[] = [];
  fieldArea = 0;
  selectedMission = new DeployedSinglePathMission();
  missionStore = getModule(MissionStore, this.$store);
  machineStore = getModule(MachineStore, this.$store);
  missionSelected = false;
  vineWidth = 0;
  volume = 0;
  sprayProduct = '';
  implements: ImplementOperationsModel[] = [];
  selectedImplement = new ImplementOperationsModel();

  windowSize = {
    x: 0,
    y: 0
  };

  private flightPathService: FlightPathServiceInterface | undefined = undefined;
  private container!: Container;

  @Watch('missionStore.fieldAreaM2')
  onWorkAreaAreaChange() {
    this.fieldArea = this.missionStore.fieldAreaHectares;
  }

  onResize() {
    this.windowSize = { x: window.innerWidth, y: window.innerHeight };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private updateRecieved(data: any): void {
    const response = data.value as string;
    const json = JSON.stringify(response);
    console.info('Mission Status Event', json);
    const status = ConvertMissionStatus.stringToMissionStatusModel(json);
    if (status.Key.machineId != this.selectedMission?.TargetMachine?.machineId) return;
    this.missionStore.setMissionStatus(status);
    if (status?.FlightPathMissionStatus.job_id != Guid.createEmpty().toString()) {
      const jobId = status?.FlightPathMissionStatus.job_id as string;
      this.missionStore.setActiveJob(jobId);
    }
  }

  created(): void {
    this.flightPathService = this.container.get<FlightPathServiceInterface>(SERVICE_IDENTIFIER.FLIGHT_PATH);

    this.addMqttVisibility(this.globalStore.missionStatusTopic, () =>
      PubSub.subscribe(this.globalStore.missionStatusTopic).subscribe({
        next: (data) => this.updateRecieved(data),
        error: (error) => this.mqttError(error)
      })
    );
  }

  async mounted() {
    if (this.flightPathService != undefined) await this.flightPathService.updateToken();
    this.onResize();
    this.loading = true;
    this.getAvailableMissions()
      .then(() => {
        this.loading = false;
      })
      .catch(() => (this.loading = true));

    await this.getImplements();
    this.missionStore.setDeployedMission(null);
    this.missionStore.selectDeployedMission(null);

    this.actions.forEach((act, key) => {
      this.subscriptions.set(key, act());
    });
  }

  destroyed() {
    this.removeMqttVisibility();
  }

  async getAvailableMissions() {
    if (this.flightPathService == undefined) return;

    const response = await this.flightPathService.getActiveDeployedSinglePathMissions(this.globalStore.accountId);
    this.missions = DeployedSinglePathMission.toModelCollection(JSON.stringify(response.data));
    this.missions = this.missions.sort((a, b) => SortHelper.compareStrings(a.MissionName as string, b.MissionName as string));
  }

  async getImplements() {
    if (this.flightPathService == undefined) return;

    const response = await this.flightPathService.getAllImplements(this.globalStore.accountId);
    this.implements = ImplementOperationsModel.toModelCollection(JSON.stringify(response.data));
  }

  getApplicationParameters() {
    if (this.selectedMission.ApplicationParameters == undefined) return;

    const params = this.selectedMission.getApplicationParameters();

    this.vineWidth = params.get(Parameters.vineWidth) != undefined ? toNumber(params.get(Parameters.vineWidth) as string) : 0;
    this.volume = params.get(Parameters.volume) != undefined ? toNumber(params.get(Parameters.volume) as string) : 0;
    this.sprayProduct = params.get(Parameters.sprayProduct) != undefined ? (params.get(Parameters.sprayProduct) as string) : '';
  }

  findImplement(): ImplementOperationsModel {
    const implement = this.implements.find((x) => x.ImplementID == this.selectedMission.ImplementCode);
    return implement == undefined ? new ImplementOperationsModel() : implement;
  }

  get selectedImplementName() {
    if (this.selectedImplement?.ImplementName == null || this.selectedImplement.ImplementName == '') return '';
    const key = 'views.missions.' + this.selectedImplement.ImplementName;

    return this.$te(key) ? this.$t(key) : '';
  }

  get isSprayingMission(): boolean {
    return (this.missionStore.applicationParameters.get(Parameters.implementType) as string) == ImplementKeys.sprayer;
  }

  get missionType() {
    if (!this.missionSelected) return '';
    if ((this.missionStore.applicationParameters.get(Parameters.implementType) as string) == ImplementKeys.sprayer) {
      return this.$t('views.missions.spraying_mission');
    } else {
      return this.$t('views.missions.tillage');
    }
  }

  get toolType() {
    const implementKey = this.missionStore.applicationParameters.get(Parameters.implementType) as string;
    return MissionToolTypes.toolTypes.filter((o) => o.value != ImplementKeys.weeder).find((x) => x.value == implementKey)?.text ?? '';
  }

  clearApplicationParameters() {
    this.vineWidth = 0;
    this.volume = 0;
    this.sprayProduct = '';
  }

  async selectMission(mission: DeployedSinglePathMission) {
    if (this.flightPathService == null) return;
    const response = await this.flightPathService.getDeployedSinglePathMission(mission.MissionDeploymentID, this.globalStore.accountId);

    if (response != null) {
      const json = JSON.stringify(response.data);
      mission = DeployedSinglePathMission.toModel(json);
    } else {
      console.error('Failed to get mission details');
      return null;
    }

    const deselect = mission != null && this.missionStore.getEditedMission == mission;
    this.missionStore.selectDeployedMission(deselect ? null : mission);

    this.selectedMission = deselect ? new DeployedSinglePathMission() : mission;
    this.missionSelected = !deselect;
    if (this.selectedMission.TargetMachine.machineId != '') {
      this.machineStore.setSelectedMachine(this.selectedMission.TargetMachine);
    }
    this.selectedImplement = deselect ? new ImplementOperationsModel() : this.findImplement();
    deselect ? this.clearApplicationParameters() : this.getApplicationParameters();
    this.updateSelected(mission.MissionDeploymentID);

    this.fetchMissionStatus();
  }

  private async fetchMissionStatus() {
    if (this.flightPathService != null) {
      const machineId = this.selectedMission.TargetMachine.machineId.toString();
      const response = await this.flightPathService.getMissionStatus(machineId as string);
      if (response != null) {
        const json = JSON.stringify(response.data);
        const status = ConvertMissionStatus.stringToMissionStatusModel(json);
        console.info('Mission Status Lambda: ', json);
        this.missionStore.setMissionStatus(status);
      }
    }
  }
}
